/**
 * API client and the TanStack Query cache.
 *
 * The snapshot is cached in IndexedDB, so a returning visitor paints the map
 * from local storage on the first frame and revalidates behind them. That
 * storage is listed in docs/cookies.md and is consent-exempt: it caches
 * content the visitor explicitly requested, nothing more.
 */
import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

/** Mirrors apps/api/src/lib/snapshot.ts. */
export interface Category {
  id: number;
  slug: string;
  name_cs: string;
  name_en: string;
  color: string;
  icon: string;
  sort_order: number;
}

export interface Pin {
  id: number;
  lat: number;
  lon: number;
  /** Ordered; `categories[0]` is primary and decides the pin's colour. */
  categories: string[];
  title: string;
  description: string;
  votes: number;
  /** False when the pin documents something rather than proposing it. */
  votable: boolean;
  source: 'user' | 'import';
  created_at: string;
}

export interface LayerInfo {
  key: string;
  label: string;
  attribution: string;
  vintage: string;
}

export interface Snapshot {
  generated_at: string;
  categories: Category[];
  pins: Pin[];
  layers: LayerInfo[];
  counts: { approved: number; votes: number };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues?: Array<{ path: string; message: string }>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { accept: 'application/json', ...init?.headers },
    // Required for the vote and admin cookies to be sent at all.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let message = `Požadavek selhal (${response.status})`;
    let issues;
    try {
      const body = (await response.json()) as { error?: string; issues?: typeof issues };
      if (body.error) message = body.error;
      issues = body.issues;
    } catch {
      // A non-JSON error body (proxy error page) keeps the generic message.
    }
    throw new ApiError(message, response.status, issues);
  }
  return (await response.json()) as T;
}

/**
 * The snapshot is served with `max-age=3600` so shared caches and CDNs can
 * hold it, but a browser's *private* cache honouring that would keep serving
 * a stale body for an hour without ever asking — which means a pin a moderator
 * approved would not appear until the hour was up. `cache: 'no-cache'` forces
 * a conditional request; the strong ETag turns it into a 304 with no body
 * whenever nothing has changed, so the saving the header was there for is kept.
 */
export const fetchSnapshot = () => request<Snapshot>('/api/v1/snapshot', { cache: 'no-cache' });

export interface SubmitPayload {
  lat: number;
  lon: number;
  categories: string[];
  title: string;
  description: string;
}

export const submitPin = (payload: SubmitPayload) =>
  request<{ id: number; status: string; message: string }>('/api/v1/pins', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const votePin = (id: number) =>
  request<{ id: number; votes: number; counted: boolean }>(`/api/v1/pins/${id}/vote`, {
    method: 'POST',
  });

export const fetchLayer = (key: string) =>
  request<GeoJSON.FeatureCollection>(`/api/v1/layers/${key}`);

/** Matches the server's Cache-Control: one hour fresh, then revalidate. */
const ONE_HOUR = 60 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: ONE_HOUR,
      gcTime: 24 * ONE_HOUR,
      retry: 1,
      refetchOnWindowFocus: false,
      /**
       * Render from cache instantly, then revalidate anyway.
       *
       * `staleTime` alone would mean no request at all for an hour, so a pin a
       * moderator has just approved would stay invisible for up to an hour.
       * `'always'` keeps the instant first paint from IndexedDB and still
       * checks — and because the snapshot carries a strong ETag, that check is
       * a 304 with an empty body whenever nothing has changed.
       */
      refetchOnMount: 'always',
    },
  },
});

/**
 * IndexedDB persistence.
 *
 * idb-keyval is the one dependency here: `indexedDB` has a callback API that
 * needs ~40 lines of promise wrapping to use safely, and this is a 600-byte
 * library that does exactly that and nothing else.
 */
export function setupPersistence(): void {
  persistQueryClient({
    queryClient,
    persister: {
      persistClient: async (client) => {
        try {
          await set('mapa-cache', client);
        } catch {
          // Private mode, disabled storage, quota — the app works without it.
        }
      },
      restoreClient: async () => {
        try {
          return await get('mapa-cache');
        } catch {
          return undefined;
        }
      },
      removeClient: async () => {
        try {
          await del('mapa-cache');
        } catch {
          /* nothing to clean up */
        }
      },
    },
    maxAge: 24 * ONE_HOUR,
    // Bump when the snapshot shape changes, so stale structures are dropped
    // rather than rendered against newer components. v2: pins carry an
    // ordered `categories` array instead of a single `category`. v3: pins
    // carry `votable` — without the bump a cached pin has it `undefined`,
    // which is falsy, and every pin on a returning visitor's map would claim
    // it cannot be supported until the refetch landed.
    buster: 'v3',
  });
}
